import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetSitesPaginatedQuery,
    type SitesFilterInput,
    SiteDeliveryType,
    SiteState,
    SiteType,
} from 'data/__generated__';
import { textColumn, booleanColumn, multiSelectColumn, dateColumn, numberColumn } from 'components/DataTable';

import { StatusTag } from 'pages/Sites/components/StatusTag';
import { TypeTag } from 'pages/Sites/components/TypeTag';
import { DeliveryTypeTag } from 'pages/Sites/components/DeliveryTypeTag';

export type SiteRow = GetSitesPaginatedQuery['sitesFilter']['sites'][number];

export const getSitesColumns = (priceRangeMap: Record<string, string>): ColumnDef<SiteRow, unknown>[] => [
    textColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'name',
        header: 'Nom',
        filterKey: 'name',
        defaultVisible: true,
    }),
    textColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'organizationName',
        header: 'Organisation',
        filterKey: 'organizationName',
        defaultVisible: true,
    }),
    textColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryZone',
        accessorFn: (row) => row.deliveryZone?.name ?? '',
        header: 'Zone',
        defaultVisible: true,
        cell: ({ row }) =>
            row.original.deliveryZone ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: 3,
                            backgroundColor: row.original.deliveryZone.color,
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                        }}
                    />
                    {row.original.deliveryZone.name}
                </span>
            ) : (
                '—'
            ),
    }),
    multiSelectColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'type',
        header: 'Type',
        filterKey: 'type',
        defaultVisible: true,
        options: Object.values(SiteType).map((type) => ({ value: type, label: type })),
        renderOption: (option) => <TypeTag type={option.value as SiteType} />,
        cell: ({ row }) => <TypeTag type={row.original.type} />,
    }),
    multiSelectColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(SiteState).map((state) => ({ value: state, label: state })),
        renderOption: (option) => <StatusTag state={option.value as SiteState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    multiSelectColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'deliveryType',
        header: 'Type de livraison',
        filterKey: 'deliveryType',
        options: Object.values(SiteDeliveryType).map((dt) => ({ value: dt, label: dt })),
        renderOption: (option) => <DeliveryTypeTag deliveryType={option.value as SiteDeliveryType} />,
        cell: ({ row }) => <DeliveryTypeTag deliveryType={row.original.deliveryType} />,
    }),
    dateColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date de création',
    }),
    dateColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'updatedAt',
        header: 'Date de mise à jour',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'isSecured',
        header: 'Sécurisé',
        filterKey: 'isSecured',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'hasExternalSetupProcess',
        header: 'Site Déporté',
        filterKey: 'hasExternalSetupProcess',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'shouldOperateOnTotops',
        header: 'Opérer sur la Totops',
        filterKey: 'shouldOperateOnTotops',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'shouldModulateForExpired',
        header: 'Modulation pour expiré',
        filterKey: 'shouldModulateForExpired',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'shouldModulateForUnknownLosses',
        header: 'Modulation pour perte inconnue',
        filterKey: 'shouldModulateForUnknownLosses',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'shouldAutomaticallyCreateTargetReassortment',
        header: 'Réassort auto',
        filterKey: 'shouldAutomaticallyCreateTargetReassortment',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'shouldAutomaticallyCreateManualReassortment',
        header: 'Réassort manuel',
        filterKey: 'shouldAutomaticallyCreateManualReassortment',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'shouldAutomaticallyFinishSiteSetup',
        header: 'Fin de setup automatique',
        filterKey: 'shouldAutomaticallyFinishSiteSetup',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'useNayaxCard',
        header: 'Use Carte Nayax',
        filterKey: 'useNayaxCard',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'hasSensei',
        header: 'Has Sensei',
        filterKey: 'hasSensei',
    }),
    numberColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'unknownShrinkageBillingThreshold',
        header: 'Seuil de perte inconnue',
        filterKey: 'unknownShrinkageBillingThreshold',
    }),
    textColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'launchDate',
        header: 'Date lancement',
    }),
    textColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'removalDate',
        header: 'Date retrait',
    }),
    numberColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'estimatedUserCount',
        header: 'Nb. Utilisateurs',
        filterKey: 'estimatedUserCount',
    }),
    textColumn<SiteRow, SitesFilterInput>({
        accessorKey: 'B2CPriceRange',
        header: 'Prix B2C',
        filterKey: 'B2CPriceRange',
        cell: ({ row }) => {
            const id = row.original.B2CPriceRange;
            return id ? priceRangeMap[id] || id : '';
        },
    }),
    // Delivery days
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDayMonday',
        accessorFn: (row) => row.deliveryDays[0],
        header: 'Livraison Lundi',
        filterKey: 'deliveryDayMonday',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDayTuesday',
        accessorFn: (row) => row.deliveryDays[1],
        header: 'Livraison Mardi',
        filterKey: 'deliveryDayTuesday',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDayWednesday',
        accessorFn: (row) => row.deliveryDays[2],
        header: 'Livraison Mercredi',
        filterKey: 'deliveryDayWednesday',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDayThursday',
        accessorFn: (row) => row.deliveryDays[3],
        header: 'Livraison Jeudi',
        filterKey: 'deliveryDayThursday',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDayFriday',
        accessorFn: (row) => row.deliveryDays[4],
        header: 'Livraison Vendredi',
        filterKey: 'deliveryDayFriday',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDaySaturday',
        accessorFn: (row) => row.deliveryDays[5],
        header: 'Livraison Samedi',
        filterKey: 'deliveryDaySaturday',
    }),
    booleanColumn<SiteRow, SitesFilterInput>({
        id: 'deliveryDaySunday',
        accessorFn: (row) => row.deliveryDays[6],
        header: 'Livraison Dimanche',
        filterKey: 'deliveryDaySunday',
    }),
];
